/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/**
 * Service for making HTTP requests to the Flask admin API backend.
 */
angular.module('nsadmin').factory('flaskApiService', ['$injector',
    function flaskApiService($injector) {

        var $http = $injector.get('$http');
        var $q = $injector.get('$q');
        var adminPermissionService = $injector.get('adminPermissionService');

        var service = {};

        /**
         * Flask API base URL - configurable via environment variable
         * Defaults to same origin as the application (proxied through nginx)
         * @type {String}
         */
        var FLASK_API_BASE = window.FLASK_API_URL || (window.location.origin + '/api');

        /**
         * Decodes a JWT token and returns the payload.
         *
         * @param {String} token
         *     The JWT token to decode.
         *
         * @returns {Object}
         *     The decoded token payload, or null if decoding fails.
         */
        function decodeJWT(token) {
            try {
                var parts = token.split('.');
                if (parts.length !== 3) {
                    return null;
                }

                var payload = parts[1];
                payload += (['', '===', '==', '='])[payload.length % 4];
                var base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

                return JSON.parse(atob(base64));
            } catch (e) {
                return null;
            }
        }

        /**
         * Makes an authenticated HTTP request to the Flask API.
         *
         * @param {String} method
         *     The HTTP method (GET, POST, PUT, DELETE).
         *
         * @param {String} endpoint
         *     The API endpoint (e.g., '/labs', '/pods').
         *
         * @param {Object} [data]
         *     Optional request body data.
         *
         * @returns {Promise}
         *     A promise that resolves with the response data or rejects with an error.
         */
        service.request = function request(method, endpoint, data) {
            var config = {
                method: method,
                url: FLASK_API_BASE + endpoint,
                headers: {
                    'Content-Type': 'application/json'
                },
                withCredentials: true  // Include cookies for CORS
            };

            // Add request body if provided
            if (data) {
                config.data = angular.copy(data);
            } else if (method === 'POST' || method === 'PUT') {
                config.data = {};
            }

            // Add user context to request body for Flask backend
            var userEmail = adminPermissionService.getUserEmail();
            var groupIds = adminPermissionService.getUserGroups();

            if (config.data) {
                config.data.user_email = userEmail;
                config.data.group_ids = groupIds;
            }

            return $http(config).then(
                function success(response) {
                    return response.data;
                },
                function error(response) {
                    var errorMessage = 'API request failed';

                    if (response.data && response.data.error) {
                        errorMessage = response.data.error;
                    } else if (response.statusText) {
                        errorMessage = response.statusText;
                    }

                    console.error('Flask API error:', {
                        method: method,
                        endpoint: endpoint,
                        status: response.status,
                        message: errorMessage
                    });

                    return $q.reject(new Error(errorMessage));
                }
            );
        };

        /**
         * Makes a GET request to the Flask API.
         *
         * @param {String} endpoint
         *     The API endpoint.
         *
         * @returns {Promise}
         *     A promise that resolves with the response data.
         */
        service.get = function get(endpoint) {
            return service.request('GET', endpoint);
        };

        /**
         * Makes a POST request to the Flask API.
         *
         * @param {String} endpoint
         *     The API endpoint.
         *
         * @param {Object} [data]
         *     Optional request body data.
         *
         * @returns {Promise}
         *     A promise that resolves with the response data.
         */
        service.post = function post(endpoint, data) {
            return service.request('POST', endpoint, data);
        };

        /**
         * Makes a PUT request to the Flask API.
         *
         * @param {String} endpoint
         *     The API endpoint.
         *
         * @param {Object} [data]
         *     Optional request body data.
         *
         * @returns {Promise}
         *     A promise that resolves with the response data.
         */
        service.put = function put(endpoint, data) {
            return service.request('PUT', endpoint, data);
        };

        /**
         * Makes a DELETE request to the Flask API.
         *
         * @param {String} endpoint
         *     The API endpoint.
         *
         * @returns {Promise}
         *     A promise that resolves with the response data.
         */
        service.delete = function deleteRequest(endpoint) {
            return service.request('DELETE', endpoint);
        };

        return service;
    }
]);
